// frontend\src\utils\filterSummary.ts
// Turns the active analytics filters into a short plain-English line, e.g.
//   "Preferred Dealers in Canada"
//   "RUCA, FLCAB sales in Canada"
//   "All sales"

export interface FilterSummaryInput {
  metric: 'sales' | 'orderCount' | 'itemCount';
  /** Comma-separated chips from the filter box (products, customers, and tag names). */
  search?: string;
  /** Comma-separated tag names. */
  tags?: string | null;
  supplier?: string | null;
  customerType?: string | null;
  customerName?: string | null;
  region?: string | null;
  /** ISO 3166-1 alpha-2 country code, as stored on the order. */
  country?: string | null;
}

const METRIC_NOUNS: Record<FilterSummaryInput['metric'], string> = {
  sales: 'sales',
  orderCount: 'orders',
  itemCount: 'items sold',
};

const splitList = (value?: string | null): string[] =>
  value ? value.split(',').map((entry) => entry.trim()).filter(Boolean) : [];

/**
 * How each customer type reads in a sentence. The names in the customer_types table are
 * adjectives describing a dealer tier ("Preferred", "Entry"), so they need a spelled-out
 * label - pluralizing them gives "Preferreds". Add an entry here for any new type; keys are
 * matched case-insensitively against the stored name.
 */
const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  preferred: 'Preferred Dealers',
  entry: 'Entry Level Dealers',
  sponsored: 'Sponsored Drivers',
  retail: 'Retail customers',
};

/** Unmapped types fall back to the stored name as-is, which is never wrong, only terse. */
const customerTypeLabel = (type: string): string => {
  const trimmed = type.trim();
  return CUSTOMER_TYPE_LABELS[trimmed.toLowerCase()] || trimmed;
};

/** Orders store the country code; show the readable name where the browser knows it. */
const countryLabel = (code: string): string => {
  const trimmed = code.trim();
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' }).of(
      trimmed.toUpperCase()
    );
    // CLDR resolves placeholder codes like "ZZ" to "Unknown Region"; the raw code is more useful.
    if (!name || /^unknown/i.test(name)) return trimmed;
    return name;
  } catch {
    return trimmed;
  }
};

export function buildFilterSummary(input: FilterSummaryInput): string {
  const tags = splitList(input.tags);
  const supplier = input.supplier?.trim() || '';
  // Tag and supplier chips also live in the search string; drop them so nothing is listed twice.
  const alreadyNamed = new Set([...tags, supplier].filter(Boolean).map((v) => v.toLowerCase()));
  const terms = splitList(input.search).filter((term) => !alreadyNamed.has(term.toLowerCase()));

  const subject = [...terms, ...tags].join(', ');
  const metricNoun = METRIC_NOUNS[input.metric];
  const audience =
    input.customerName?.trim() ||
    (input.customerType ? customerTypeLabel(input.customerType) : '');
  // Country is the deeper drill-down level, so it wins over the region it sits in.
  const place = input.country ? countryLabel(input.country) : input.region?.trim() || '';

  const parts: string[] = [];
  if (subject) {
    parts.push(`${subject} ${metricNoun}`);
    if (supplier) parts.push(`from ${supplier}`);
    if (audience) parts.push(`to ${audience}`);
  } else if (supplier) {
    parts.push(`${supplier} ${metricNoun}`);
    if (audience) parts.push(`to ${audience}`);
  } else if (audience) {
    // Nothing narrows the products, so the audience is the whole story.
    parts.push(audience);
  } else {
    parts.push(`All ${metricNoun}`);
  }
  if (place) parts.push(`in ${place}`);

  return parts.join(' ');
}
